import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { X, Send, FileText, Paperclip, Loader2, Languages, FileImage, FileArchive, File, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'inglese', label: 'Inglese' },
  { code: 'francese', label: 'Francese' },
  { code: 'tedesco', label: 'Tedesco' },
  { code: 'spagnolo', label: 'Spagnolo' },
  { code: 'portoghese', label: 'Portoghese' },
  { code: 'olandese', label: 'Olandese' },
  { code: 'russo', label: 'Russo' },
  { code: 'cinese', label: 'Cinese' },
  { code: 'giapponese', label: 'Giapponese' },
  { code: 'arabo', label: 'Arabo' },
  { code: 'polacco', label: 'Polacco' },
];

function fileIcon(name = '') {
  const ext = name.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return <FileImage size={12} />;
  if (['zip','rar','7z','gz','tar'].includes(ext)) return <FileArchive size={12} />;
  return <FileText size={12} />;
}
function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
}

// Dropdown shown when typing an address
function SuggestionDropdown({ suggestions, onSelect }) {
  return (
    <div
      className="absolute left-14 right-0 top-full mt-1 z-50 rounded-lg border shadow-lg overflow-hidden"
      style={{ background: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}
    >
      {suggestions.map((c, i) => (
        <button
          key={i}
          data-testid={`suggest-${c.email}`}
          onMouseDown={e => { e.preventDefault(); onSelect(c); }}
          className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white"
            style={{ background: '#E2725B' }}>
            {(c.name || c.email).charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            {c.name && <div className="font-medium truncate text-xs">{c.name}</div>}
            <div className="text-muted-foreground text-xs truncate">{c.email}</div>
          </div>
          {c.email_count > 0 && (
            <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">{c.email_count} email</span>
          )}
        </button>
      ))}
    </div>
  );
}

export default function ComposeModal() {
  const { accounts, composeOpen, composeData, closeCompose, sendEmail, saveDraft } = useApp();
  const [form, setForm] = useState({ accountId: '', to: '', cc: '', ccn: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showCcn, setShowCcn] = useState(false);
  const [translateLang, setTranslateLang] = useState('inglese');
  const [translating, setTranslating] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [signature, setSignature] = useState('');
  const [showSignature, setShowSignature] = useState(true);
  const fileInputRef = useRef(null);

  const [contacts, setContacts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestFor, setSuggestFor] = useState(null); // 'to' | 'cc' | 'ccn'
  const suggestRef = useRef(null);

  // Fetch signature whenever the account changes
  const fetchSignature = async (accountId) => {
    if (!accountId) return;
    try {
      const res = await axios.get(`${API}/signatures/${accountId}`);
      setSignature(res.data.signature || '');
    } catch {
      setSignature('');
    }
  };

  // Fetch rubrica contacts once when compose opens
  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/rubrica`);
      setContacts(res.data.contacts || []);
    } catch {
      setContacts([]);
    }
  };

  useEffect(() => {
    if (composeOpen) {
      const defaultAccount = composeData?.accountId || accounts[0]?.id || '';
      setForm({
        accountId: defaultAccount,
        to: composeData?.to || '',
        cc: composeData?.cc || '',
        ccn: composeData?.ccn || '',
        subject: composeData?.subject || '',
        body: composeData?.body || '',
      });
      setShowCc(!!composeData?.cc);
      setShowCcn(!!composeData?.ccn);
      setAttachments([]);
      setShowSignature(true);
      setSuggestions([]);
      setSuggestFor(null);
      fetchSignature(defaultAccount);
      fetchContacts();
    }
  }, [composeOpen, composeData, accounts]); // eslint-disable-line

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setSuggestions([]);
        setSuggestFor(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!composeOpen) return null;

  // When account selector changes mid-compose
  const handleAccountChange = (newAccountId) => {
    setForm(prev => ({ ...prev, accountId: newAccountId }));
    fetchSignature(newAccountId);
  };

  // Autocomplete helpers
  const getSuggestions = (value) => {
    if (!value || value.length < 2) return [];
    const term = value.split(/[,;]/).pop().trim().toLowerCase();
    if (!term || term.length < 2) return [];
    return contacts
      .filter(c =>
        c.email.toLowerCase().includes(term) ||
        (c.name || '').toLowerCase().includes(term)
      )
      .slice(0, 6);
  };

  const handleAddressInput = (field_name, value) => {
    field(field_name, value);
    const sugg = getSuggestions(value);
    setSuggestions(sugg);
    setSuggestFor(sugg.length > 0 ? field_name : null);
  };

  const applySuggestion = (contact, field_name) => {
    const current = form[field_name] || '';
    const parts = current.split(/[,;]/);
    parts[parts.length - 1] = ` ${contact.name ? contact.name + ' <' + contact.email + '>' : contact.email}`;
    const newVal = parts.join(',').trimStart();
    field(field_name, newVal + ', ');
    setSuggestions([]);
    setSuggestFor(null);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      setAttachments(prev => [...prev, { id: tempId, filename: file.name, size: file.size, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/compose/upload-attachment`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setAttachments(prev => prev.map(a =>
          a.id === tempId ? { ...res.data, uploading: false } : a
        ));
      } catch {
        setAttachments(prev => prev.filter(a => a.id !== tempId));
        toast.error(`Errore caricamento: ${file.name}`);
      }
    }
    e.target.value = '';
  };

  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const handleTranslateBody = async () => {
    if (!form.body.trim()) { toast.error('Scrivi il testo prima di tradurre'); return; }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/translate`, { text: form.body, target_language: translateLang });
      field('body', res.data.translated);
      toast.success(`Testo tradotto in ${LANGUAGES.find(l => l.code === translateLang)?.label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante la traduzione');
    } finally {
      setTranslating(false);
    }
  };

  const handleSend = async () => {
    if (!form.accountId) { toast.error('Seleziona un account mittente'); return; }
    if (!form.to.trim()) { toast.error('Inserisci un destinatario'); return; }
    if (!form.subject.trim()) { toast.error('Inserisci un oggetto'); return; }
    setSending(true);
    // Append signature if active
    const bodyWithSig = showSignature && signature
      ? `${form.body}\n\n--\n${signature}`
      : form.body;
    try {
      await sendEmail({
        account_id: form.accountId,
        to_addr: form.to,
        cc_addr: form.cc || null,
        bcc_addr: form.ccn || null,
        subject: form.subject,
        body: bodyWithSig,
        reply_to_message_id: composeData?.replyToMessageId || null,
        attachment_ids: attachments.filter(a => !a.uploading).map(a => a.attachment_id || a.id),
      });
      toast.success('Email inviata con successo');
      closeCompose();
    } catch (e) {
      toast.error(`Errore invio: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!form.accountId) { toast.error('Seleziona un account'); return; }
    setSavingDraft(true);
    const bodyWithSig = showSignature && signature
      ? `${form.body}\n\n--\n${signature}`
      : form.body;
    try {
      await saveDraft({
        account_id: form.accountId,
        to_addr: form.to,
        cc_addr: form.cc || null,
        bcc_addr: form.ccn || null,
        subject: form.subject,
        body: bodyWithSig,
      });
      toast.success('Bozza salvata');
      closeCompose();
    } catch (e) {
      toast.error(`Errore bozza: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSavingDraft(false);
    }
  };

  const field = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const selectedAccount = accounts.find(a => a.id === form.accountId);

  const inputStyle = {
    borderColor: 'hsl(var(--border))',
    background: 'transparent',
    fontFamily: 'Manrope, sans-serif',
    color: 'hsl(var(--foreground))',
    outline: 'none',
    width: '100%',
  };

  return (
    <div
      data-testid="compose-modal-overlay"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => e.target === e.currentTarget && closeCompose()}
    >
      <div
        data-testid="compose-modal"
        className="w-full max-w-2xl rounded-sm border shadow-2xl animate-fade-in-up"
        style={{
          background: 'hsl(var(--card))',
          borderColor: 'hsl(var(--border))',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <h2 className="font-semibold text-sm">
            {composeData?.mode === 'reply' ? 'Rispondi' : composeData?.mode === 'forward' ? 'Inoltra' : 'Nuova email'}
          </h2>
          <button
            data-testid="compose-close-btn"
            onClick={closeCompose}
            className="p-1 rounded-sm hover:bg-accent transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3">
            <div className="flex items-center gap-3 border-b pb-3" style={{ borderColor: 'hsl(var(--border))' }}>
            <span className="text-xs text-muted-foreground w-14 flex-shrink-0">Da:</span>
            <div className="relative flex-1">
              <select
                data-testid="compose-from-select"
                value={form.accountId}
                onChange={e => handleAccountChange(e.target.value)}
                className="w-full text-sm py-1 pr-6 appearance-none focus:outline-none"
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Seleziona account...</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.email}</option>
                ))}
              </select>
              {selectedAccount && (
                <span
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
                  style={{ background: selectedAccount.color }}
                />
              )}
            </div>
          </div>

          {/* To */}
          <div className="relative border-b pb-3" style={{ borderColor: 'hsl(var(--border))' }} ref={suggestFor === 'to' ? suggestRef : null}>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-14 flex-shrink-0">A:</span>
              <input
                data-testid="compose-to-input"
                type="text"
                value={form.to}
                onChange={e => handleAddressInput('to', e.target.value)}
                onFocus={e => handleAddressInput('to', e.target.value)}
                placeholder="destinatario@email.com, altro@email.com"
                className="flex-1 text-sm py-1 focus:outline-none"
                style={inputStyle}
                autoComplete="off"
              />
            </div>
            {suggestFor === 'to' && suggestions.length > 0 && (
              <SuggestionDropdown suggestions={suggestions} onSelect={c => applySuggestion(c, 'to')} />
            )}
          </div>

          {/* CC/CCN toggle buttons row */}
          <div className="flex items-center justify-end gap-2 -mt-1">
            <button data-testid="compose-toggle-cc" onClick={() => setShowCc(!showCc)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors">Cc</button>
            <button data-testid="compose-toggle-ccn" onClick={() => setShowCcn(!showCcn)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors">CCN</button>
          </div>

          {/* Cc (optional) */}
          {showCc && (
            <div className="relative border-b pb-3" style={{ borderColor: 'hsl(var(--border))' }} ref={suggestFor === 'cc' ? suggestRef : null}>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-14 flex-shrink-0">Cc:</span>
                <input data-testid="compose-cc-input" type="text" value={form.cc}
                  onChange={e => handleAddressInput('cc', e.target.value)}
                  onFocus={e => handleAddressInput('cc', e.target.value)}
                  placeholder="cc@email.com, altro@email.com"
                  className="flex-1 text-sm py-1 focus:outline-none" style={inputStyle} autoComplete="off" />
              </div>
              {suggestFor === 'cc' && suggestions.length > 0 && (
                <SuggestionDropdown suggestions={suggestions} onSelect={c => applySuggestion(c, 'cc')} />
              )}
            </div>
          )}

          {/* CCN (optional) */}
          {showCcn && (
            <div className="relative border-b pb-3" style={{ borderColor: 'hsl(var(--border))' }} ref={suggestFor === 'ccn' ? suggestRef : null}>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-14 flex-shrink-0">CCN:</span>
                <input data-testid="compose-ccn-input" type="text" value={form.ccn}
                  onChange={e => handleAddressInput('ccn', e.target.value)}
                  onFocus={e => handleAddressInput('ccn', e.target.value)}
                  placeholder="ccn@email.com, altro@email.com"
                  className="flex-1 text-sm py-1 focus:outline-none" style={inputStyle} autoComplete="off" />
              </div>
              {suggestFor === 'ccn' && suggestions.length > 0 && (
                <SuggestionDropdown suggestions={suggestions} onSelect={c => applySuggestion(c, 'ccn')} />
              )}
            </div>
          )}

          {/* Subject */}
          <div className="flex items-center gap-3 border-b pb-3" style={{ borderColor: 'hsl(var(--border))' }}>
            <span className="text-xs text-muted-foreground w-14 flex-shrink-0">Oggetto:</span>
            <input
              data-testid="compose-subject-input"
              type="text"
              value={form.subject}
              onChange={e => field('subject', e.target.value)}
              placeholder="Oggetto del messaggio"
              className="flex-1 text-sm py-1 focus:outline-none"
              style={inputStyle}
            />
          </div>

          {/* Body */}
          <textarea
            data-testid="compose-body-textarea"
            value={form.body}
            onChange={e => field('body', e.target.value)}
            placeholder="Scrivi il tuo messaggio..."
            className="w-full min-h-[200px] text-sm p-0 resize-y focus:outline-none"
            style={{ ...inputStyle, border: 'none' }}
          />

          {/* Signature block */}
          {signature && (
            <div className="border-t pt-2" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground font-mono">--</span>
                <button
                  data-testid="compose-toggle-sig"
                  onClick={() => setShowSignature(v => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showSignature ? 'Nascondi firma' : 'Mostra firma'}
                </button>
              </div>
              {showSignature && (
                <pre
                  data-testid="compose-signature-preview"
                  className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {signature}
                </pre>
              )}
            </div>
          )}

          {/* Translation bar */}
          <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
            <Languages size={13} className="text-muted-foreground flex-shrink-0" />
            <span className="text-xs text-muted-foreground flex-shrink-0">Traduci in:</span>
            <select
              data-testid="compose-translate-lang"
              value={translateLang}
              onChange={e => setTranslateLang(e.target.value)}
              className="text-xs border rounded px-2 py-1 focus:outline-none flex-1 max-w-[140px]"
              style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--background))' }}
            >
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <button
              data-testid="compose-translate-btn"
              onClick={handleTranslateBody}
              disabled={translating || !form.body.trim()}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium border rounded transition-all hover:bg-accent"
              style={{ borderColor: 'hsl(var(--border))', opacity: translating || !form.body.trim() ? 0.5 : 1 }}
            >
              {translating ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
              {translating ? 'Traduco...' : 'Traduci'}
            </button>
          </div>
        </div>

        {/* Footer: attach + actions */}
        <div className="flex flex-col gap-2 px-4 sm:px-5 py-3 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Attach */}
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
            <button
              data-testid="compose-attach-btn"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-sm transition-all hover:bg-accent"
              style={{ borderColor: 'hsl(var(--border))' }}
              title="Allega file"
            >
              <Paperclip size={13} /> Allega
            </button>

            <div className="flex-1 min-w-0" />

            <button
              data-testid="compose-draft-btn"
              onClick={handleSaveDraft}
              disabled={savingDraft}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-sm transition-all hover:bg-accent"
              style={{ borderColor: 'hsl(var(--border))', opacity: savingDraft ? 0.7 : 1 }}
            >
              {savingDraft ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
              {savingDraft ? 'Salvataggio...' : 'Bozza'}
            </button>

            <button
              data-testid="compose-send-btn"
              onClick={handleSend}
              disabled={sending || attachments.some(a => a.uploading)}
              className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2 text-xs sm:text-sm font-medium text-white rounded-sm transition-all"
              style={{ background: '#E2725B', opacity: sending ? 0.7 : 1 }}
              onMouseEnter={e => !sending && (e.currentTarget.style.background = '#d4644d')}
              onMouseLeave={e => e.currentTarget.style.background = '#E2725B'}
            >
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {sending ? 'Invio...' : 'Invia'}
            </button>
          </div>

          {/* Attached files list */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {attachments.map(att => (
                <div key={att.id}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs"
                  style={{ borderColor: 'hsl(var(--border))', background: 'hsl(40,15%,97%)' }}
                >
                  <span style={{ color: '#E2725B' }}>{fileIcon(att.filename)}</span>
                  <span className="truncate max-w-[120px]">{att.filename}</span>
                  {att.size ? <span className="text-gray-400">{formatBytes(att.size)}</span> : null}
                  {att.uploading
                    ? <Loader2 size={10} className="animate-spin text-gray-400 ml-0.5" />
                    : <button onClick={() => removeAttachment(att.id)} className="text-gray-400 hover:text-red-500 transition-colors ml-0.5"><X size={10} /></button>
                  }
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
